<template>
  <v-dialog
    v-model="dialog"
    max-width="500"
    persistent
    @click:outside="closeModal"
  >
    <v-card class="contact-dealer-modal">
      <!-- Header -->
      <v-card-title class="modal-header">
        <div class="header-content">
          <div class="vehicle-info">
            <h3 class="vehicle-title">{{ getVehicleTitle() }}</h3>
            <p class="vehicle-price">{{ formatPrice(vehicle.amount) }}</p>
          </div>
          <v-btn
            icon
            @click="closeModal"
            class="close-btn"
          >
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Chat Messages -->
      <v-card-text class="chat-container">
        <div class="messages-container" ref="messagesContainer">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message-wrapper"
            :class="message.sender_type"
          >
            <div class="message-bubble">
              <div class="message-text" v-html="formatMessage(message.message)"></div>
              <div class="message-time">{{ formatTime(message.sent_at) }}</div>
            </div>
          </div>
          
          <!-- Typing indicator -->
          <div v-if="isTyping" class="message-wrapper assistant">
            <div class="message-bubble typing">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Input Area -->
      <v-card-actions class="input-area">
        <v-text-field
          v-model="currentMessage"
          placeholder="Type your message..."
          outlined
          dense
          hide-details
          class="message-input"
          @keyup.enter="sendMessage"
          :disabled="isTyping"
        >
          <template v-slot:append>
            <v-btn
              icon
              color="primary"
              @click="sendMessage"
              :disabled="!currentMessage.trim() || isTyping"
              class="send-btn"
            >
              <v-icon class="material-icons-round">send</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  name: 'ContactDealerModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicle: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      messages: [],
      currentMessage: '',
      isTyping: false,
      sessionId: null,
      currentStep: 'greeting',
      conversationStarted: false
    }
  },
  watch: {
    value(newVal) {
      this.dialog = newVal
      if (newVal && !this.conversationStarted) {
        this.startConversation()
      }
    },
    dialog(newVal) {
      this.$emit('input', newVal)
    }
  },
  methods: {
    async startConversation() {
      try {
        this.conversationStarted = true
        this.sessionId = this.generateSessionId()
        
        // Start with greeting message
        await this.sendContactDealerMessage('', 'greeting')
        
      } catch (error) {
        console.error('Error starting conversation:', error)
      }
    },

    async sendMessage() {
      if (!this.currentMessage.trim() || this.isTyping) return

      const userMessage = this.currentMessage.trim()
      this.currentMessage = ''

      // Add user message to chat
      this.addMessage({
        sender_type: 'user',
        message: userMessage,
        sent_at: new Date().toISOString()
      })

      // Send to API
      await this.sendContactDealerMessage(userMessage, this.currentStep)
    },

    async sendContactDealerMessage(message, step) {
      try {
        this.isTyping = true

        const response = await axios.post('/api/digital-assistant/contact-dealer', {
          vehicle_id: this.vehicle.id,
          session_id: this.sessionId,
          message: message,
          step: step
        })

        if (response.data.success) {
          // Add assistant response
          this.addMessage({
            sender_type: 'assistant',
            message: response.data.message,
            sent_at: new Date().toISOString()
          })

          // Update current step
          this.currentStep = response.data.step
        }

      } catch (error) {
        console.error('Error sending message:', error)
        this.addMessage({
          sender_type: 'assistant',
          message: "I'm here to help you with this vehicle. Let me get some information to connect you with our team. What's your first name?",
          sent_at: new Date().toISOString()
        })
      } finally {
        this.isTyping = false
        this.scrollToBottom()
      }
    },

    addMessage(message) {
      this.messages.push(message)
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },

    formatMessage(message) {
      // Convert line breaks to <br> and handle basic formatting
      return message.replace(/\n/g, '<br>')
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'
      
      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)
      
      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return '$0'
      
      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return '$0'
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    generateSessionId() {
      return 'contact_dealer_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11)
    },

    closeModal() {
      this.dialog = false
      // Reset conversation state
      this.messages = []
      this.currentMessage = ''
      this.conversationStarted = false
      this.currentStep = 'greeting'
      this.sessionId = null
    }
  }
}
</script>

<style scoped>
/* Modal Styles */
.contact-dealer-modal {
  border-radius: 12px !important;
  overflow: hidden;
}

.modal-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 20px !important;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.vehicle-info {
  flex: 1;
}

.vehicle-title {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
  color: #111827 !important;
  margin: 0 !important;
  line-height: 1.3;
}

.vehicle-price {
  font-size: 1rem;
  font-weight: 700;
  color: #059669;
  margin: 4px 0 0 0;
}

.close-btn {
  margin-left: 16px;
}

/* Chat Container */
.chat-container {
  padding: 0 !important;
  height: 400px;
  display: flex;
  flex-direction: column;
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #fafafa;
}

/* Messages */
.message-wrapper {
  margin-bottom: 16px;
  display: flex;
}

.message-wrapper.user {
  justify-content: flex-end;
}

.message-wrapper.assistant {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 18px;
  position: relative;
}

.user .message-bubble {
  background: #1976d2;
  color: white;
  border-bottom-right-radius: 4px;
}

.assistant .message-bubble {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.message-text {
  font-size: 0.875rem;
  line-height: 1.4;
  word-wrap: break-word;
}

.message-time {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 4px;
}

/* Typing Indicator */
.typing {
  background: white !important;
  border: 1px solid #e5e7eb !important;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #9ca3af;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* Input Area */
.input-area {
  padding: 16px 20px !important;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.message-input {
  flex: 1;
}

.message-input >>> .v-input__control {
  min-height: 44px !important;
}

.message-input >>> .v-text-field__details {
  display: none;
}

.send-btn {
  margin-left: 8px;
}

/* Responsive */
@media (max-width: 600px) {
  .contact-dealer-modal {
    margin: 0;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0 !important;
  }
  
  .chat-container {
    height: calc(100vh - 140px);
  }
  
  .message-bubble {
    max-width: 90%;
  }
}
</style>
